/**
 * humanize — plain-language helpers for the RDL tiles + previews.
 *
 * Strict scope rule (per Bill 2026-04-23): documentId / GUIDs / officer codes
 * never reach the UI. These helpers convert the raw SP fields into
 * conversational text the FSR can read at a glance.
 */

const DOC_CODE_LABELS: Record<string, string> = {
  DL_FRONT: 'Driver License — Front',
  DL_BACK: 'Driver License — Back',
  PASSPORT: 'Passport',
  STATE_ID: 'State ID Card',
  PROOF_ADDR: 'Proof of Address',
  SSN_CARD: 'Social Security Card',
  W9: 'W-9',
  TRUST_CERT: 'Trust Certification',
  BIZ_RES: 'Business Resolution',
  EIN_LETTER: 'EIN Letter',
  IRA_BENE: 'IRA Beneficiary',
  DISC_PKG: 'Disclosure Package',
  DEPOSIT_AGMT: 'Deposit Agreement',
  SIG_CARD: 'Signature Card',
  ADV_ACTION: 'Adverse Action Notice',
  OTHER: 'Document',
};

export function humanizeDocCode(docCode: string): string {
  if (DOC_CODE_LABELS[docCode]) return DOC_CODE_LABELS[docCode]!;
  // Fall back to title-casing the underscored code.
  return docCode
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0) + p.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Convert an uploader email or system identifier into a human name.
 *   "ssmith@mypcb.com"          → "Sarah Smith"  (first letters expanded)
 *   "bwiggins@mypcb.com"        → "Bill Wiggins"
 *   "signnow-webhook"           → "SignNow"
 *   "rao-pipeline"              → "RAO automation"
 *   "rcc-adverse-action"        → "Compliance"
 *   "customer-portal"           → "Customer (portal)"
 *
 * The FSR-roster mapping below is intentionally tiny — when the staff
 * directory ships from `/v3/config/users` we replace this with a real lookup.
 */
const STAFF_DIRECTORY: Record<string, string> = {
  'ssmith@mypcb.com': 'Sarah Smith',
  'bwiggins@mypcb.com': 'Bill Wiggins',
  'jdoe@mypcb.com': 'Jordan Doe',
};

const SYSTEM_LABELS: Record<string, string> = {
  'signnow-webhook': 'SignNow',
  'rao-pipeline': 'New Acct automation',
  'rcc-adverse-action': 'Compliance',
  'customer-portal': 'Customer (portal)',
  'system': 'System',
};

export function humanizeUploader(uploader: string): string {
  if (!uploader) return 'Unknown';
  const lower = uploader.toLowerCase();
  if (STAFF_DIRECTORY[lower]) return STAFF_DIRECTORY[lower]!;
  if (SYSTEM_LABELS[lower]) return SYSTEM_LABELS[lower]!;
  // If it looks like an email, use the local-part with title-case spaces on
  // dot/dash separators. Avoids leaking the @mypcb.com domain in the UI.
  if (uploader.includes('@')) {
    const localPart = uploader.split('@')[0] ?? uploader;
    return localPart
      .split(/[.\-_]/)
      .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ''))
      .filter(Boolean)
      .join(' ');
  }
  return uploader;
}

/** Return "Today", "Yesterday", "3 days ago", or short month-day for older. */
export function formatRelativeDay(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const now = new Date();
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Display-friendly customer label inferred from the docs we have. Today the
 * SP list doesn't surface customer name on the document row, so we fall back
 * to "CIF NNNN" until the join with CustInq lands on the server side.
 */
export function customerLabelFromCif(cifId: string): string {
  if (!cifId) return 'Not linked to a customer';
  return `CIF ${cifId}`;
}

/**
 * Customer label for a bucket. Prefers the resolved name the server now
 * carries on each document (customerName), then the CIF. The empty-CIF bucket
 * says what it IS - documents nothing has linked to a customer record - rather
 * than "Unknown customer", which read as though the app had lost track of
 * someone (Bill 2026-09-11).
 */
export function customerLabel(cifId: string, customerName?: string | null): string {
  // Name-centric (Bill 2026-09-11): the NAME is the label; the CIF is the
  // subtitle the user confirms with (customerSubtitle). Only when no name is
  // known does the CIF carry the label.
  const n = (customerName ?? '').trim();
  if (n) return n;
  return customerLabelFromCif(cifId);
}

/** The confirming line under a name-centric label: "CIF 1234567" (empty when the label already IS the CIF). */
export function customerSubtitle(cifId: string, customerName?: string | null): string {
  const n = (customerName ?? '').trim();
  return n && cifId ? `CIF ${cifId}` : '';
}
